/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Link, useParams, useNavigate, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../core'

import {
  KTCard,
  KTCardBody,
  KTCardHeader,
  Notes,
  DeleteModal,
  Select,
  Loading,
} from '../../../components'
import {useDispatch, useSelector} from 'react-redux'
import {
  subcontractorDocumentGet,
  subcontractorDocumentDelete,
  subcontractorDocumentUpdate,
  subcontractorDocumentCreateFile,
  subcontractorDocumentMailContent,
  subcontractorDocumentSendMail,
  setDeleted,
  setFileAdded,
  setMailSent,
  setRedirect,
} from '../../../redux/slices/subcontractordocument.slice'
import {basicLookup} from '../../../redux/slices/lookup.slice'
import {SourceType, KTSVG} from '../../../helpers'
import Dropdown from 'react-bootstrap/Dropdown'
import {Modal} from 'react-bootstrap'

import * as Yup from 'yup'
import {useFormik} from 'formik'

import {useMemoizedPageUrl, useMemoizedBreadcrumbs} from '../../../utils/performanceOptimizations'

const FILE_ACCEPT =
  'image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const selectSubcontractorDocumentData = (state) => ({
  detail: state.subcontractorDocument.detail,
  isDeleted: state.subcontractorDocument.isDeleted,
  isRedirect: state.subcontractorDocument.isRedirect,
  isFileAdded: state.subcontractorDocument.isFileAdded,
  mailContent: state.subcontractorDocument.mailContent,
  isMailSent: state.subcontractorDocument.isMailSent,
  error: state.subcontractorDocument.error,
  loading: state.subcontractorDocument.loading,
})

const selectLookupData = (state) => ({
  SubcontractorDocumentState: state.lookup.SubcontractorDocumentState,
})

const stateBadgeClass = (stateId) => {
  switch (stateId) {
    case 1:
      return 'badge badge-light-warning'
    case 2:
      return 'badge badge-light-danger'
    case 3:
      return 'badge badge-light-success'
    default:
      return 'badge badge-light'
  }
}

function Detail() {
  const [show, setShow] = useState(false)
  const [showMail, setShowMail] = useState(false)
  const [isCollapse, setIsCollapse] = useState(true)
  const [recipients, setRecipients] = useState('')
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()
  const {detail, isDeleted, isRedirect, isFileAdded, mailContent, isMailSent, error, loading} =
    useSelector(selectSubcontractorDocumentData)
  const {SubcontractorDocumentState} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const pageUrl = useMemoizedPageUrl(location)

  useEffect(() => {
    dispatch(setDeleted(false))
    dispatch(setFileAdded(false))
    dispatch(setMailSent(false))
    dispatch(setRedirect(false))
    dispatch(basicLookup('SubcontractorDocumentState'))
  }, [dispatch])

  useEffect(() => {
    if (id) dispatch(subcontractorDocumentGet({Id: Number(id)}))
  }, [dispatch, id])

  useEffect(() => {
    if (detail) {
      formik.setFieldValue('SubcontractorDocumentStateId', detail.subcontractorDocumentStateId || '')
      formik.setFieldValue('Description', detail.description || '')
    }
  }, [detail])

  const handleDelete = useCallback(() => {
    dispatch(subcontractorDocumentDelete({Id: Number(id)}))
  }, [dispatch, id])

  useEffect(() => {
    if (isDeleted) navigate(`/${pageUrl[1]}/${pageUrl[2]}`)
  }, [isDeleted, navigate, pageUrl])

  useEffect(() => {
    if (isRedirect) {
      dispatch(setRedirect(false))
      dispatch(subcontractorDocumentGet({Id: Number(id)}))
    }
  }, [isRedirect, dispatch, id])

  useEffect(() => {
    if (isFileAdded) {
      dispatch(setFileAdded(false))
      dispatch(subcontractorDocumentGet({Id: Number(id)}))
    }
  }, [isFileAdded, dispatch, id])

  useEffect(() => {
    if (mailContent) {
      setRecipients(mailContent.recipients || '')
    }
  }, [mailContent])

  useEffect(() => {
    if (isMailSent) {
      dispatch(setMailSent(false))
      setShowMail(false)
    }
  }, [isMailSent, dispatch])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT'}),
        path: '/projects/subcontractor-document',
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl]
  )

  const validSchema = Yup.object().shape({
    SubcontractorDocumentStateId: Yup.string().required(
      intl.formatMessage({id: 'REQUIRED.GENERAL'})
    ),
  })

  const formik = useFormik({
    initialValues: {
      Id: Number(id),
      SubcontractorDocumentStateId: '',
      Description: '',
    },
    validationSchema: validSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      dispatch(subcontractorDocumentUpdate(values))
    },
  })

  const fileFormik = useFormik({
    initialValues: {
      Id: Number(id),
      FormFile: null,
      Description: '',
    },
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      dispatch(subcontractorDocumentCreateFile(values))
      resetForm()
    },
  })

  const handleOpenMail = useCallback(() => {
    dispatch(subcontractorDocumentMailContent({Id: Number(id)}))
    setShowMail(true)
  }, [dispatch, id])

  const handleSendMail = useCallback(() => {
    dispatch(subcontractorDocumentSendMail({Id: Number(id), Recipients: recipients}))
  }, [dispatch, id, recipients])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT.DETAIL'})}
      </PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>
                              {detail.companyTitle}
                            </div>

                            <div className='ms-5'></div>
                          </div>

                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                            {detail?.projectName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/nav/Projects.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.projectName}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary align-self-center'
                            onClick={handleOpenMail}
                          >
                            <KTSVG
                              path='/images/icons/duotune/communication/com002.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.SENDMAIL'})}
                          </button>
                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                          <Dropdown autoClose='outside' align={{lg: 'end'}}>
                            <Dropdown.Toggle
                              variant='secondary'
                              size='sm'
                              className='iconless ps-3 pe-3'
                            >
                              <KTSVG
                                path='/images/icons/duotune/general/gen053.svg'
                                className='svg-icon-2'
                              />
                            </Dropdown.Toggle>

                            <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                              <Dropdown.Item
                                onClick={() => setShow(true)}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                </div>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.PERIOD'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {detail.year} /{' '}
                              {intl.formatDate(new Date(2000, (detail.month || 1) - 1, 1), {
                                month: 'long',
                              })}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.STATE'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              <span className={stateBadgeClass(detail.subcontractorDocumentStateId)}>
                                {detail.subcontractorDocumentStateName}
                              </span>
                            </div>
                          </div>

                          {detail?.createdBy && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.CREATEDBY'})}
                              </div>
                              <div className='fw-bold fs-4'>{detail.createdBy}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {detail?.description && (
                    <>
                      <div
                        className='notice d-flex rounded border-primary border border-dashed p-6'
                        style={{whiteSpace: 'pre-wrap'}}
                      >
                        {detail?.description}
                      </div>
                    </>
                  )}
                </KTCardBody>
              </KTCard>

              <KTCard>
                <KTCardHeader
                  title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.FILES'})}
                />
                <KTCardBody className='p-9'>
                  <form className='form' onSubmit={fileFormik.handleSubmit} noValidate>
                    <div className='row align-items-end'>
                      <div className='col-lg-4 fv-row fv-plugins-icon-container'>
                        <label className='fw-bold fs-6 mb-2 required'>
                          {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.ADDFILE'})}
                        </label>
                        <label
                          htmlFor='SubcontractorDocumentFile'
                          className='btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden d-flex align-items-center justify-content-center'
                        >
                          <input
                            className='position-absolute z-index-2 opacity-0 w-1px h-1px'
                            type='file'
                            accept={FILE_ACCEPT}
                            id='SubcontractorDocumentFile'
                            name='FormFile'
                            multiple
                            onChange={(event) =>
                              fileFormik.setFieldValue('FormFile', event.currentTarget.files)
                            }
                          />
                          <KTSVG
                            path='/images/icons/duotune/communication/com008.svg'
                            className='svg-icon-3 position-relative'
                          />
                          {fileFormik.values.FormFile && fileFormik.values.FormFile.length > 0
                            ? Array.from(fileFormik.values.FormFile)
                                .map((file) => file.name)
                                .join(', ')
                            : intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.CHOOSEFILE'})}
                        </label>
                      </div>
                      <div className='col-lg-5 fv-row fv-plugins-icon-container'>
                        <label className='fw-bold fs-6 mb-2'>
                          {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.FILEDESCRIPTION'})}
                        </label>
                        <input
                          type='text'
                          className='form-control form-control-solid'
                          autoComplete='off'
                          disabled={fileFormik.isSubmitting}
                          {...fileFormik.getFieldProps('Description')}
                        />
                      </div>
                      <div className='col-lg-3 fv-row fv-plugins-icon-container'>
                        <button
                          type='submit'
                          className='btn btn-primary'
                          disabled={fileFormik.isSubmitting || !fileFormik.values.FormFile}
                        >
                          {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.UPLOAD'})}
                        </button>
                      </div>
                    </div>
                  </form>

                  <div className='separator separator-dashed my-6'></div>

                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start fw-bolder fs-7 text-uppercase gs-0'>
                          <th>
                            {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.FILENAME'})}
                          </th>
                          <th>
                            {intl.formatMessage({
                              id: 'SUBCONTRACTORDOCUMENT.DETAIL.FILEDESCRIPTION',
                            })}
                          </th>
                          <th>
                            {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.UPLOADEDBY'})}
                          </th>
                          <th>
                            {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.UPLOADDATE'})}
                          </th>
                          <th className='text-end'></th>
                        </tr>
                      </thead>
                      <tbody className='text-gray-600 fw-semibold'>
                        {detail.files && detail.files.length > 0 ? (
                          detail.files.map((file, index) => (
                            <tr key={index}>
                              <td>{file.name}</td>
                              <td>{file.description}</td>
                              <td>
                                {file.createdBy ||
                                  intl.formatMessage({
                                    id: 'SUBCONTRACTORDOCUMENT.DETAIL.UPLOADER.SUBCONTRACTOR',
                                  })}
                              </td>
                              <td>
                                {file.createdDate &&
                                  intl.formatDate(new Date(file.createdDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                  })}
                              </td>
                              <td className='text-end'>
                                {file.fileUrl && (
                                  <a
                                    href={file.fileUrl}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='btn btn-sm btn-light btn-color-muted btn-active-light-success px-4 py-2'
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/files/fil021.svg'
                                      className='svg-icon-3'
                                    />
                                    {intl.formatMessage({
                                      id: 'SUBCONTRACTORDOCUMENT.DETAIL.DOWNLOAD',
                                    })}
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className='text-center'>
                              {intl.formatMessage({id: 'NON_RESULT'})}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </KTCardBody>
              </KTCard>
            </>
          )}
        </div>

        <div className='col-xl-3'>
          <KTCard className='mb-5'>
            <KTCardHeader
              title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.STATE'})}
              collapsed={isCollapse}
              onCollapse={(event) => setIsCollapse(event)}
            />

            <KTCardBody className='ps-10 pe-10 pt-5 pb-0' collapseble isShow={isCollapse}>
              <form
                className='form position-relative mb-6'
                onSubmit={formik.handleSubmit}
                noValidate
              >
                <div className='row mb-6'>
                  <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.STATE'})}
                  </label>
                  <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                    <Select
                      data={SubcontractorDocumentState}
                      required
                      {...formik.getFieldProps('SubcontractorDocumentStateId')}
                      onChange={(item) =>
                        formik.setFieldValue('SubcontractorDocumentStateId', Number(item?.value))
                      }
                    />
                    {formik.touched.SubcontractorDocumentStateId &&
                      formik.errors.SubcontractorDocumentStateId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.SubcontractorDocumentStateId}</span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                <div className='text-center'>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </KTCardBody>
          </KTCard>

          <Notes SourceId={Number(id)} SourceTypeId={SourceType.SubcontractorDocument} />
        </div>
      </div>

      <DeleteModal
        onClick={handleDelete}
        onOpen={show}
        onClose={() => setShow(false)}
        error={error}
      />

      <Modal
        show={showMail}
        onHide={() => setShowMail(false)}
        size='lg'
        centered
        aria-labelledby='subcontractor-document-mail-modal'
      >
        <div className='modal-content'>
          <div className='d-flex align-items-center justify-content-between py-5 ps-8 pe-5 border-bottom'>
            <h5 className='fw-bold m-0'>
              {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.MAIL.TITLE'})}
            </h5>
            <div
              className='btn btn-icon btn-sm btn-light-primary ms-2'
              onClick={() => setShowMail(false)}
            >
              <KTSVG className='svg-icon-1' path='/images/icons/duotune/arrows/arr061.svg' />
            </div>
          </div>

          <div className='py-5 ps-8 pe-8'>
            <div className='mb-6'>
              <label className='fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.MAIL.RECIPIENTS'})}
              </label>
              <input
                type='text'
                className='form-control form-control-solid'
                autoComplete='off'
                placeholder={intl.formatMessage({
                  id: 'SUBCONTRACTORDOCUMENT.MAIL.RECIPIENTS.PLACEHOLDER',
                })}
                value={recipients}
                onChange={(event) => setRecipients(event.target.value)}
              />
            </div>

            <div className='mb-6'>
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.MAIL.CONTENT'})}
              </label>
              <div
                className='border rounded p-5 mh-400px overflow-auto bg-light'
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{__html: (mailContent && mailContent.content) || ''}}
              />
            </div>
          </div>

          <div className='d-flex align-items-center justify-content-end py-5 ps-8 pe-8 border-top gap-3'>
            <button className='btn btn-light' onClick={() => setShowMail(false)}>
              {intl.formatMessage({id: 'BUTTON.CLOSE'})}
            </button>
            <button
              className='btn btn-primary'
              onClick={handleSendMail}
              disabled={!recipients || loading}
            >
              {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.MAIL.SEND'})}
            </button>
          </div>
        </div>
      </Modal>

      {loading && <Loading />}
    </>
  )
}
export {Detail}
